import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { pusdtAgreementBunja } from '@flipster/features/src/earn-page/get-premium-usdt/bunja';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { usdBalanceEmptyThreshold } from '@flipster/features/src/shared/earn/state';
import { useRouter } from '@flipster/i18n/routing';
import { useOverlay, useOverlayItem } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { createStepComponent } from '@flipster/react/misc/step/Steps';
import { useSteps } from '@flipster/react/misc/step/use-steps';
import { walletsBunja } from '@flipster/socket/r230522/wallet';
import { css } from '@flipster/styled-system/css';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { UsdtPremiumEvent } from '@flipster/utils/segment/events/usdt-premium';

import AccessPremiumEarnModal from '../access-premium-earn-modal';
import ManagePusdtModal from '../manage-pusdt-modal';
import InfoStep from './info-step';
import TermsStep from './terms-step';

type AgreementSteps = {
  info: undefined;
  terms: undefined;
};
const Steps = createStepComponent<AgreementSteps>();

const PusdtNudgeModal = () => {
  const overlay = useOverlay();
  const router = useRouter();
  const { onClose } = useOverlayItem();
  const { current, push } = useSteps<AgreementSteps>({
    initialValue: {
      step: 'info',
      context: undefined,
    },
  });

  const { isValidKycAtom } = useBunja(authBunja);
  const { isAgreedPusdtAtom } = useBunja(pusdtAgreementBunja);
  const { walletsAtom } = useBunja(walletsBunja);

  const isValidKyc = useAtomValue(isValidKycAtom);
  const isAgreed = useAtomValue(isAgreedPusdtAtom);
  const wallets = useAtomValue(walletsAtom);
  const usdtWalletBalance = Num(wallets?.[UsdtCurrency]?.walletBalanceExclWithdrawalPending ?? 0);

  const goToTerms = () => {
    push({ step: 'terms', context: undefined });
  };

  const goToInfo = () => {
    push({ step: 'info', context: undefined });
  };

  const onClickNext = () => {
    segment.track(UsdtPremiumEvent.AddUsdtPremiumClick);
    if (!isAgreed) {
      goToTerms();
      return;
    }
    if (usdtWalletBalance.lt(usdBalanceEmptyThreshold)) {
      overlay.open(<AccessPremiumEarnModal />);
    } else {
      overlay.open(<ManagePusdtModal />);
    }
  };

  const onClickContinue = () => {
    onClose();
    if (usdtWalletBalance.lt(usdBalanceEmptyThreshold)) {
      overlay.open(<AccessPremiumEarnModal />);
      return;
    }
    overlay.open(<ManagePusdtModal />);
  };

  if (!isValidKyc) {
    return (
      <Modal>
        <Modal.Content>
          <h2 className={css({ textStyle: 'Title/S', color: 'content.default.level-4', mb: 16 })}>
            Identity verification
          </h2>
          <p>Required for deposit, withdrawal and trading.</p>
        </Modal.Content>
        <Modal.Footer>
          <Button hierarchy='secondary' color='neutral' label='Close' onClick={onClose} />
          <Button
            label='Verify now'
            onClick={() => {
              router.push('/user/kyc');
              onClose();
            }}
          />
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal size='large'>
      <Steps model={current}>
        <Modal.Close onClose={onClose} />
        <Steps.Step step='info'>{() => <InfoStep goToTerms={goToTerms} onClickNext={onClickNext} />}</Steps.Step>
        <Steps.Step step='terms'>{() => <TermsStep goToInfo={goToInfo} onClose={onClickContinue} />}</Steps.Step>
      </Steps>
    </Modal>
  );
};

export default PusdtNudgeModal;
